
import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { RealtimeUpdateContext, useRealtimeUpdateProvider } from "@/hooks/useRealtimeUpdates";
import { CallSessionProvider } from "@/hooks/useCallSession";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import PublicRoute from "@/components/auth/PublicRoute";
import GlobalCallOverlay from "@/components/GlobalCallOverlay";
import Login from "./pages/Login";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { useVersionCheck } from "./hooks/useVersionCheck";

const Analytics = lazy(() => import("./pages/Analytics"));
const Attendance = lazy(() => import("./pages/Attendance"));
const PaidPending = lazy(() => import("./pages/PaidPending"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));

// Create a more optimized QueryClient with better caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Reduce background refetching to prevent unnecessary reloads
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false, // Prevent refetch when switching tabs
      refetchOnMount: false, // Prevent refetch on component mount if data exists
      refetchOnReconnect: 'always', // Only refetch on network reconnect
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
    },
  },
});

// Realtime Update Provider Component
const RealtimeUpdateProvider = ({ children }: { children: React.ReactNode }) => {
  const realtimeUpdateProvider = useRealtimeUpdateProvider();
  
  return (
    <RealtimeUpdateContext.Provider value={realtimeUpdateProvider}>
      {children}
    </RealtimeUpdateContext.Provider>
  );
};

function App() {
  useVersionCheck();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <RealtimeUpdateProvider>
              <CallSessionProvider>
                <Suspense fallback={null}>
                <Routes>
                  <Route path="/login" element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  } />
                  <Route path="/" element={
                    <ProtectedRoute>
                      <Index />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin-settings" element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminSettings />
                    </ProtectedRoute>
                  } />
                  <Route path="/paid-pending" element={
                    <ProtectedRoute>
                      <PaidPending />
                    </ProtectedRoute>
                  } />
                  <Route path="/analytics" element={
                    <ProtectedRoute>
                      <Analytics />
                    </ProtectedRoute>
                  } />
                  {FEATURE_FLAGS.SHOW_ATTENDANCE && (
                    <Route path="/attendance" element={
                      <ProtectedRoute>
                        <Attendance />
                      </ProtectedRoute>
                    } />
                  )}
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
                <GlobalCallOverlay />
              </CallSessionProvider>
            </RealtimeUpdateProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
